// Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { ERC20 } from '@typechain-types/index'
import { ethers, Contract } from 'ethers'
import { Erc20Abi } from './Abis'
import { ChainConfiguration } from './DeploymentConfigTypes'
import { EthAddr } from './SchemaTypes'
import { ERC20Wei } from './conversions'

export type LiquidityAsset = {
  chainConfigName: string
  name: string
  symbol: string
  addr: EthAddr
  decimals: number
}

export interface ERC20BalanceInfo {
  address: string
  balance: string
  decimals: number
  symbol: string
  name: string
}

export type ChainAccountValues = Record<EthAddr, ERC20Wei>
export async function getLiquidityAssetForAddress(
  assetAddr: EthAddr,
  chainConfiguration: ChainConfiguration
): Promise<LiquidityAsset> {
  const provider = new ethers.JsonRpcProvider(chainConfiguration.backendProviderUrl, undefined, { staticNetwork: true })

  const erc20Contract = new Contract(assetAddr, Erc20Abi, provider) as unknown as ERC20
  const name = await erc20Contract.name()
  const symbol = await erc20Contract.symbol()
  const decimals = Number(await erc20Contract.decimals())
  return { chainConfigName: chainConfiguration.chainConfigurationName, name, symbol, decimals, addr: assetAddr }
}

/**
 * Loads ERC20 balance for a specific account address
 * @param contractAddress ERC20 contract address
 * @param accountAddress Account address to check balance for
 * @param provider Ethers provider instance
 * @returns Balance information for the account
 */
export async function getERC20Balance(
  contractAddress: string,
  accountAddress: string,
  provider: ethers.Provider
): Promise<ERC20BalanceInfo> {
  try {
    const contract = new Contract(contractAddress, Erc20Abi, provider)
    const [balance, decimals, symbol, name] = await Promise.all([
      contract.balanceOf(accountAddress),
      contract.decimals(),
      contract.symbol(),
      contract.name(),
    ])

    return {
      address: contractAddress,
      balance: balance.toString(),
      decimals: Number(decimals),
      symbol,
      name,
    }
  } catch (error) {
    throw new Error(
      `Failed to load balance for ${contractAddress} account ${accountAddress}: ${error instanceof Error ? error.message : String(error)}`
    )
  }
}
